import type { UrlLocation } from '@seek/chalice-types';
import type { Hubble } from '@seek/hubble/lib/Hubble';
import { metrics } from '@seek/metrics-js';
import type { Action, Location } from 'history';
import { matchRoutes } from 'react-router-config';

import type { AnalyticsFacade } from 'src/modules/AnalyticsFacade';
import { setHubbleLoginId } from 'src/modules/hubble';
import { logger } from 'src/modules/logger';
import { jobDetailsPageRegex } from 'src/modules/routes-regexp';
import { isAuthenticated } from 'src/modules/seek-jobs-api-client/apis/candidate';
import appRoutesConfig from 'src/routes';
import type createStore from 'src/store/createStore';
import { locationChanged } from 'src/store/location';
import { clearNewSince } from 'src/store/results';
import { selectAuthenticated, selectLocation } from 'src/store/selectors';
import { updateAuthenticated } from 'src/store/user';

import { fetchDataForRoutes } from './fetchDataForRoutes';
import { routerLocationToUrlLocation } from './locationTransforms';

const ALLOWED_QUERY_KEYS = ['tags', 'jobId', 'type', 'pos'];

/** Returns an array of keys whose values changed between prevQuery and nextQuery */
function findChangedQueryParams(
  prevQuery: Record<string, string | undefined>,
  nextQuery: Record<string, string | undefined>,
): string[] {
  const allKeys = new Set([
    ...Object.keys(prevQuery || {}),
    ...Object.keys(nextQuery || {}),
  ]);
  const changedKeys: string[] = [];

  for (const key of allKeys) {
    if (prevQuery[key] !== nextQuery[key]) {
      changedKeys.push(key);
    }
  }
  return changedKeys;
}

/** Returns true if *every* changed key is in the ALLOWED_QUERY_KEYS list */
function onlyAllowedKeysChanged(changedKeys: string[]): boolean {
  return changedKeys.every((key) => ALLOWED_QUERY_KEYS.includes(key));
}

interface Options {
  analyticsFacade: AnalyticsFacade;
  store: ReturnType<typeof createStore>;
  apolloClient: Parameters<typeof fetchDataForRoutes>[0]['apolloClient'];
  visitorId: string;
  hubble: Hubble;
}

export const createLocationChangeHandler = (options: Options) => {
  const { analyticsFacade, store, apolloClient, visitorId, hubble } = options;
  const { dispatch } = store;
  const onLocationChanged = (
    location: Location<UrlLocation['state']>,
    action?: Action,
  ): void => {
    if (action === 'REPLACE') {
      return;
    }

    metrics.count('pageload');
    const state = store.getState();
    const currentLocation = selectLocation(state);
    const nextLocation = routerLocationToUrlLocation(location);
    const {
      pathname: currentPathname,
      query: {
        page: currentPageNumber,
        advertiserid: currentAdvertiserId,
      } = {},
    } = currentLocation;
    const {
      pathname: nextPathname,
      query: { page: nextPageNumber, advertiserid: nextAdvertiserId } = {},
    } = nextLocation;

    const isJobDetails = jobDetailsPageRegex.test(nextPathname!);
    const didPageChange =
      currentPathname !== nextPathname ||
      currentPageNumber !== nextPageNumber ||
      currentAdvertiserId !== nextAdvertiserId ||
      isJobDetails;

    if (action !== 'POP' && didPageChange) {
      window.document.body.scrollIntoView();
    }

    /*
     * Compare the entire query objects to see if
     * *some* keys changed that are *not* in ALLOWED_QUERY_KEYS.
     * Also, if the pathname changed, always clear the stickiness
     */
    const changedKeys = findChangedQueryParams(
      currentLocation.query ?? {},
      nextLocation.query ?? {},
    );
    const pathnameChanged = currentPathname !== nextPathname;

    if (pathnameChanged) {
      // If the pathname changes, we lose stickiness in search
      dispatch(clearNewSince());
    } else if (changedKeys.length > 0 && !onlyAllowedKeysChanged(changedKeys)) {
      // If there are changed keys and any of them is disallowed, clear
      dispatch(clearNewSince());
    }

    // Continue with the usual route-matching logic
    const matchedRoutes = matchRoutes(
      appRoutesConfig(state.appConfig.site),
      nextPathname!,
    ).map(({ route }) => route);
    const authenticated = selectAuthenticated(state);

    dispatch(locationChanged(window.location.href, nextLocation));

    if (typeof action !== 'undefined') {
      analyticsFacade.locationChanged();
    }

    const waitForAuth = async () => {
      if (!authenticated) {
        metrics.count('auth.event', ['event:lastKnownSolIdNotSet']);
        const resolvedAuthState = await isAuthenticated();
        const payload = { authenticated: resolvedAuthState };
        dispatch(updateAuthenticated(payload));
        analyticsFacade.userDetailsUpdated(payload);
        setHubbleLoginId(hubble, store);
        return resolvedAuthState;
      }
      return authenticated;
    };

    fetchDataForRoutes({
      analyticsFacade,
      matchedRoutes,
      location: nextLocation,
      store,
      dispatch,
      getState: store.getState,
      apolloClient,
      waitForAuth,
      visitorId,
    }).catch((err: Error) => {
      logger.error(err);
    });
  };

  return onLocationChanged;
};
